import React, { useState } from 'react';
import { Lock, Eye, EyeOff, X, AlertTriangle, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal de suppression de compte (utilisateur connecté).
 * Conforme RGPD art. 17 + Apple App Store Review 5.1.1(v).
 * Requiert re-saisie du mot de passe + confirmation textuelle.
 */
const DeleteAccountModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { token, logout, user } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const CONFIRM_KEYWORD = t('deleteAccount.confirmKeyword', 'SUPPRIMER');
  const isConfirmed = confirmationText.trim().toUpperCase() === CONFIRM_KEYWORD.toUpperCase();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isConfirmed || !password) return;
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/delete-account`,
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('deleteAccount.success', 'Votre compte a été définitivement supprimé.'));
      // Purge session locale et retour Landing
      setTimeout(() => {
        logout();
        navigate('/', { replace: true });
      }, 1200);
    } catch (error) {
      const message = error.response?.data?.detail || t('deleteAccount.error', 'Erreur lors de la suppression du compte.');
      toast.error(message);
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setPassword('');
    setConfirmationText('');
    setShowPassword(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[2500] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={handleClose}
          data-testid="delete-account-modal"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#18181B] border border-red-900/40 rounded-2xl w-full max-w-md p-6 relative"
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-zinc-400 hover:text-white"
              data-testid="close-delete-account-btn"
              disabled={loading}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-red-500/10 border border-red-500/40 flex items-center justify-center">
                <AlertTriangle className="w-7 h-7 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {t('deleteAccount.title', 'Supprimer mon compte')}
              </h2>
              <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
                {t(
                  'deleteAccount.subtitle',
                  'Cette action est irréversible. Ton compte, tes préférences et ton abonnement actif seront définitivement supprimés.'
                )}
              </p>
            </div>

            <div className="mb-5 p-4 bg-red-950/30 border border-red-900/40 rounded text-xs text-zinc-300 space-y-2">
              <p className="font-semibold text-red-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {t('deleteAccount.impactTitle', 'Ce qui va se passer :')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-zinc-400">
                <li>{t('deleteAccount.impact1', 'Ton profil, ton email et ton téléphone sont effacés définitivement.')}</li>
                <li>{t('deleteAccount.impact2', 'Ton abonnement actif prend fin immédiatement (aucun remboursement automatique).')}</li>
                <li>{t('deleteAccount.impact3', "L'historique de trajets est anonymisé (obligation légale de conservation).")}</li>
                <li>{t('deleteAccount.impact4', 'Tu ne pourras plus te reconnecter avec cet email.')}</li>
              </ul>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm">
                  {t('deleteAccount.passwordLabel', 'Ton mot de passe actuel')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('deleteAccount.passwordPlaceholder', 'Mot de passe')}
                    className="pl-10 pr-10 bg-zinc-900 border-zinc-700 text-white h-11"
                    required
                    autoComplete="current-password"
                    data-testid="delete-account-password-input"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm">
                  {t('deleteAccount.typeToConfirm', 'Tape {{keyword}} pour confirmer', { keyword: CONFIRM_KEYWORD })}
                </Label>
                <Input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder={CONFIRM_KEYWORD}
                  className="bg-zinc-900 border-zinc-700 text-white h-11 font-mono"
                  required
                  data-testid="delete-account-confirm-input"
                  disabled={loading}
                />
              </div>

              <div className="flex flex-col gap-2 pt-2">
                <Button
                  type="submit"
                  disabled={loading || !isConfirmed || !password}
                  className="w-full bg-red-600 text-white font-bold h-11 hover:bg-red-700 disabled:bg-red-900/40 disabled:text-zinc-500"
                  data-testid="submit-delete-account-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      {t('deleteAccount.submit', 'Supprimer définitivement')}
                    </span>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClose}
                  disabled={loading}
                  className="w-full text-zinc-400 hover:text-white h-11"
                  data-testid="cancel-delete-account-btn"
                >
                  {t('common.cancel', 'Annuler')}
                </Button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DeleteAccountModal;
